<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('notificationTemplate.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('notificationTemplate.pageTitle'),
            href: '/notification-templates'
          },
          {
            text: $t('edit'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-md-3">
            <ts-loading-banner :loading="loading">
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="required">{{ $t('language') }}</label>
                    <a-select v-model.number="model.lang_id" :options="languages" class="ts-w-full"></a-select>
                    <div class="validate" v-if="errors.has('lang_id')">{{ errors.first('lang_id') }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="required">{{ $t('notificationTemplate.notType') }}</label>
                    <a-select
                      v-model.number="model.not_type_id"
                      :options="notification_types"
                      class="ts-w-full"
                    ></a-select>
                    <div
                      class="validate"
                      v-if="errors.has('not_type_id')"
                    >{{ errors.first('not_type_id') }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="required">{{ $t('notificationTemplate.subject') }}</label>
                    <textarea v-model.trim="model.not_subject" rows="3" class="form-control"></textarea>
                    <div
                      class="validate"
                      v-if="errors.has('not_subject')"
                    >{{ errors.first('not_subject') }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
								<div class="col-md-4">
									<div class="form-group">
										<ts-checkbox
											v-model="model.is_public"
                       @change="() => model.channel_list = []"
										>{{ $t('notificationTemplate.isPublic') }}</ts-checkbox>
									</div>
								</div>
							</div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label :class="model.is_public ? 'required' : ''">{{ $t('notificationTemplate.channel') }}</label>
                    <a-select
                      v-model="model.channel_list"
                      mode="multiple"
                      :options="channel_lists"
                      class="ts-w-full"
                    ></a-select>
                    <div
                      class="validate"
                      v-if="errors.has('channel_list')"
                    >{{ errors.first('channel_list') }}</div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <ts-checkbox v-model="model.is_active">{{ $t('notificationTemplate.active') }}</ts-checkbox>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <h2 class="ts-text-gray-500 ts-text-lg ts-mt-4">{{ $t('notificationTemplate.parameter') }}</h2>
                    <div class="ts-border ts-w-full ts-h-40 ts-p-3 ts-rounded-md">
                      <div class="ts-grid ts-grid-cols-3 ts-gap-3">
                        <div
                          v-for="(pa, index) in not_params"
                          :key="index"
                          class="ts-font-robot ts-font-semibold"
                        >
                          <input type="text" :value="pa" class="ts-outline-none" readonly>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ts-loading-banner>
          </div>
          <div class="col-md-9">
            <label class="required">{{ $t('notificationTemplate.content') }}</label>
            <VueEditor
              v-model="model.not_content"
              :editor-toolbar="customToolbar"
              useCustomImageHandler
            />
            <div class="validate" v-if="errors.has('not_content')">{{ errors.first('not_content') }}</div>
          </div>
        </div>
        <div class="row ts-mt-5">
          <div class="col-md-12 text-right ts-space-x-3">
            <ts-button @click.prevent="$router.push({ name: 'notification-template' })">{{ $t('cancel') }}</ts-button>
            <ts-button
              color="primary"
              :waiting="waiting"
              @click.prevent="onUpdate"
            >{{ $t('update') }}</ts-button>
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
export default {
  name: 'notificationTemplateEdit',
  components: {
    VueEditor
  },
  data() {
    return {
      loading: false,
      waiting: false,
      errors: new Errors(),
      languages: [],
      notification_types: [],
      channel_lists: [],
      not_params: [],
      model: {
        lang_id: null,
        not_type_id: null,
        not_subject: null,
        not_content: null,
        channel_list: [],
        is_active: false,
        is_public: false
      },
      customToolbar: [
        [{ font: [] }],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ header: 1 }, { header: 2 }],
        ['blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['link', 'code-block'],
        [{ direction: 'rtl' }],
        ['clean']
      ]
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('administrator/notificationTemplate', ['edit_data'])
  },
  methods: {
    ...mapActions('administrator/notificationTemplate', [
      'getLang',
      'getNotType',
      'getNotChannel',
      'find',
      'getNotParam',
      'update'
    ]),
    async fetchData() {
      this.loading = true
      await this.find(this.$route.params.id).then(() => {
        this.setEdit()
      })
      await this.getLang().then(response => {
        this.languages = response.data.map(el => ({
          value: el.lang_id,
          label: el.language_name
        }))
      })
      await this.getNotType().then(response => {
        this.notification_types = response.data.map(el => ({
          value: el.not_type_id,
          label: el.not_types
        }))
      })
      await this.getNotChannel()
        .then(response => {
          this.channel_lists = response.data.map(el => ({
            value: el.channel,
            label: el.channel
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onUpdate() {
      this.waiting = true
      this.update({
        id: this.$route.params.id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'notification-template' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onGetParams(){
      this.loading = true
      this.getNotParam({
        filter: {
          not_type_id: this.model.not_type_id
        }
      })
      .then(response => {
        this.not_params = response.data.map(el => el.parameter )
      })
      .finally(() => {
        this.loading = false
      })
    },
    setEdit() {
      if (!_.isEmpty(this.edit_data)) {
        this.model.lang_id = this.edit_data.lang_id
        this.model.not_type_id = this.edit_data.not_type_id
        this.model.not_subject = this.edit_data.not_subject
        this.model.not_content = this.edit_data.not_content
        this.model.channel_list = this.edit_data.channel_list ? this.edit_data.channel_list.split(',') : []
        this.model.is_active = this.edit_data.is_active
        this.model.is_public = this.edit_data.is_public
        this.onGetParams()
      }
    }
  }
}
</script>

<style>
.ql-editor {
  min-height: 23vh !important;
  height: 64vh !important;
  overflow-y: scroll !important;
}
</style>
